import React, { useState } from "react";
import { Modal, Row, Col, Table } from "react-bootstrap";
import moment from "moment";

//utils
import { shortDateFormat } from '../../../utils/dateFormat';
import { DEFAULT_VISIBILITY_WEEKS } from '../../../utils/constants';
import { DATE_FORMAT } from "./RosterScheduler";

//Components
import Button from '../../../components/CustomButton/CustomButton';
import DatePicker from '../../../components/DatePicker/DatePicker';
import CustomRadio from "../../../components/CustomRadio/CustomRadio";
import CustomCheckbox from "../../../components/CustomCheckbox/CustomCheckbox";
import Input, { Textarea } from "../../../elements/Input";
import SwitchWidget from "../ControlPanel/SwitchWidget";
import PatternStartWeekEmployeeSelect from "./WorkPatternAssignment/PatternStartWeekEmployeeSelect";

//Assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';

const SKIP_REASON_LABEL = {
    multi_pattern: 'Multiple Pattern Assignments',
};

const EditWorkPatternModal = ({ show, handleClose, applyWorkPatternData, handleUpdate, handleSave, onConfirmAnyway, conflicts = [], isSaving, cycleWeekLength, dateTimeRules, visibilityWeeks, isPm, t }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [activeWeek, setActiveWeek] = useState(1);
    const [conflictEmployeeSearch, setConflictEmployeeSearch] = useState("");
    if (!applyWorkPatternData) return null;

    const filteredEmployees = applyWorkPatternData.employees?.filter(emp => {
        const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase());
    }) || [];

    const cycleWeekOffsets = applyWorkPatternData.cycleWeekOffsets || {};
    const totalWeeks = cycleWeekLength || 1;

    const weekCounts = {};
    for (let w = 1; w <= totalWeeks; w++) weekCounts[w] = 0;
    Object.values(cycleWeekOffsets).forEach(w => { if (w >= 1 && w <= totalWeeks) weekCounts[w]++; });

    const handleCheckboxChange = (userId, checked) => {
        const current = applyWorkPatternData.selectedUserIds || [];
        const updatedIds = checked ? [...current, userId] : current.filter(id => id !== userId);

        const newOffsets = Object.assign({}, cycleWeekOffsets);
        if (checked && totalWeeks > 1)
            newOffsets[userId] = activeWeek;
        else
            delete newOffsets[userId];

        handleUpdate({ selectedUserIds: updatedIds, cycleWeekOffsets: newOffsets });
    };

    const handleSelectAllEmployees = () => {
        const allIds = applyWorkPatternData.employees.map(e => e.user_id);
        const isAllSelected = applyWorkPatternData.selectedUserIds?.length === allIds.length;
        if (isAllSelected) {
            handleUpdate({ selectedUserIds: [], cycleWeekOffsets: {} });
        } else {
            const newOffsets = Object.assign({}, cycleWeekOffsets);
            if (totalWeeks > 1) {
                allIds.forEach(id => { newOffsets[id] = activeWeek; });
            }
            handleUpdate({ selectedUserIds: allIds, cycleWeekOffsets: newOffsets });
        }
    };

    const skippableConflicts = conflicts.filter(c => c.skip_reason);
    const replaceableConflicts = conflicts.filter(c => !c.skip_reason);
    const filteredConflicts = conflicts.filter(c =>
        (c.employee_name || '').toLowerCase().includes(conflictEmployeeSearch.toLowerCase())
    );
    const skippedEmployeeCount = new Set(skippableConflicts.map(c => c.employee_name)).size;
    const hasConflicts = conflicts.length > 0;
    const allSkipped = hasConflicts && replaceableConflicts.length === 0;

    const isTrueSubset = applyWorkPatternData.isSubset && applyWorkPatternData.selectedUserIds?.length > 0 && applyWorkPatternData.selectedUserIds.length < applyWorkPatternData.employees?.length;
    const isBlockedSubset = allSkipped && isTrueSubset;

    const getProceedLabel = () => {
        if (isSaving) return t('Saving...');
        if (allSkipped && !isTrueSubset) return t('Proceed (Future Assignments Only)');
        if (replaceableConflicts.length > 0) return `${t('Proceed')} (${replaceableConflicts.length} ${t('Cycles will be replaced')})`;
        return t('Apply Changes');
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered className="modal-with-sticky-footer">
            <Modal.Header>
                <Modal.Title className="fs14 fw-700">
                    {t('Edit')} {t('Work Pattern')}
                </Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" alt='close icon' onClick={handleClose} />
            </Modal.Header>

            <Modal.Body className="px-4" style={{ maxHeight: 'calc(100vh - 75px)', overflowY: 'auto' }}>
                <div>
                    <label className="fw-600 fs14 mb-1 d-block">{t('Select Employees')}</label>
                    <small className="text-muted mb-1 d-block">{t('Apply this change to all employees assigned to this work pattern or choose specific ones.')}</small>
                    <div className="d-flex gap-3 p-2">
                        {!isPm && (
                            <CustomRadio
                                name="applyType"
                                checked={applyWorkPatternData.isSubset === false}
                                onChange={() =>
                                    handleUpdate({ isSubset: false, selectedUserIds: applyWorkPatternData.employees.map(e => e.user_id) })
                                }
                                label={`${t('All')} ${t('Users')} (${applyWorkPatternData.employees?.length})`}
                            />
                        )}
                        <CustomRadio
                            name="applyType"
                            checked={applyWorkPatternData.isSubset}
                            onChange={() => handleUpdate({ isSubset: true, selectedUserIds: [], cycleWeekOffsets: {} })}
                            label={`${t('Select')} ${t('Users')}`}
                        />
                    </div>
                </div>

                <Row className="g-3">
                    {applyWorkPatternData.isSubset && (
                        <Col md={5}>
                            <div className="weekly-shift-weeks-container">
                                <div className="d-flex justify-content-between align-items-center p-2 weekly-shift-weeks-header">
                                    <span className="fw-bold fs14">
                                        {t('Members')} ({applyWorkPatternData.employees?.length})
                                    </span>
                                </div>
                                <div className="quick-planner-weeks-body" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    <div className="conversation-search mb-2">
                                        <input
                                            type="text"
                                            className="conversation-search-input"
                                            placeholder={t('Search')}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            value={searchQuery}
                                        />
                                        <i className="fa fa-search conversation-search-icon" />
                                    </div>

                                    {totalWeeks > 1 && (
                                        <div className="px-2 mb-2">
                                            <PatternStartWeekEmployeeSelect
                                                weekCount={totalWeeks}
                                                activeWeek={activeWeek}
                                                onWeekTabClick={setActiveWeek}
                                                weekCounts={weekCounts}
                                                t={t}
                                            />
                                        </div>
                                    )}

                                    <div className="px-2 mb-1">
                                        <span
                                            className="fs12 cursor-pointer text-primary-color fw-600"
                                            onClick={handleSelectAllEmployees}
                                        >
                                            {applyWorkPatternData.selectedUserIds?.length === applyWorkPatternData.employees.length ? `${t('Clear')} ${t('All')}` : t('Select All')}
                                        </span>
                                    </div>

                                    <div className="px-2 d-flex flex-column gap-2">
                                        {filteredEmployees.length > 0 ? (
                                            filteredEmployees.map(emp => {
                                                const isChecked = applyWorkPatternData.selectedUserIds?.includes(emp.user_id);
                                                const empWeek = cycleWeekOffsets[emp.user_id];
                                                return (
                                                    <div key={emp.user_id} className="d-flex align-items-center justify-content-between">
                                                        <CustomCheckbox
                                                            isChecked={!!isChecked}
                                                            onChange={(checked) => handleCheckboxChange(emp.user_id, checked)}
                                                            label={`${emp.first_name || ''} ${emp.last_name || ''}`.trim()}
                                                            wrapperClassName="mb-0 fs12"
                                                        />
                                                        {isChecked && totalWeeks > 1 && empWeek && (
                                                            <span className="badge ms-1 flex-shrink-0 text-white fw-normal small" style={{ background: '#2550AC' }}>
                                                                {t('Start')} {t('Week')} {empWeek}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p className="text-muted fs12 text-center">{t('No Data Found')}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    )}

                    <Col md={applyWorkPatternData.isSubset ? 7 : 12}>
                        <div className="d-flex flex-column gap-3">
                            <DatePicker
                                label={t('Apply Changes From')}
                                minDate={moment().add(1, 'day').toDate()}
                                selected={applyWorkPatternData.selectedDate ? moment(applyWorkPatternData.selectedDate).toDate() : null}
                                onChange={(date) => handleUpdate({ selectedDate: date ? moment(date).format(DATE_FORMAT) : '' })}
                                popperProps={{ strategy: 'fixed' }}
                                popperPlacement="bottom-start"
                            />

                            {applyWorkPatternData.isSubset && applyWorkPatternData.selectedUserIds?.length > 0 && applyWorkPatternData.selectedUserIds?.length < applyWorkPatternData.employees.length && (
                                <>
                                    <Input
                                        label={t('New Template Name')}
                                        value={applyWorkPatternData.newTemplateName}
                                        onChange={(e) =>
                                            handleUpdate({ newTemplateName: e.target.value })
                                        }
                                        placeholder={t('e.g. High Season')}
                                    />
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <SwitchWidget
                                            value={!!applyWorkPatternData.isAutomation}
                                            onChange={(checked) =>
                                                handleUpdate({ isAutomation: checked })
                                            }
                                        />
                                        <span className="fw-600 fs13">{t('Automation')}</span>
                                    </div>
                                </>
                            )}

                            {applyWorkPatternData.selectedDate &&
                                moment(applyWorkPatternData.selectedDate).isBefore(moment().add((visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days'), 'day') && (
                                    <div>
                                        <label className='form-label fw-600 fs13'>{t('Reason for Short Notice Change')}<span className="text-danger">*</span></label>
                                        <Textarea
                                            className="mt-1"
                                            rows={2}
                                            value={applyWorkPatternData.overrideReason}
                                            error={!applyWorkPatternData.overrideReason?.trim()}
                                            onChange={(e) =>
                                                handleUpdate({ overrideReason: e.target.value })
                                            }
                                        />
                                    </div>
                                )}

                            <p className="text-muted fs12">{t('Shifts before this date will remain unchanged. Future shifts will be updated.')}</p>
                        </div>
                    </Col>
                </Row>

                {hasConflicts && (
                    <div className="mt-2 d-flex flex-column gap-2">
                        {skippableConflicts.length > 0 && replaceableConflicts.length > 0 && (
                            <div className="d-flex gap-3 fs12 border-start ps-2">
                                <span className="text-warning">
                                    <strong>{skippedEmployeeCount}</strong> {t('Employee(s) will be skipped')}
                                </span>
                                <span className="text-success">
                                    <strong>{replaceableConflicts.length}</strong> {t('Cycle(s) will be replaced')}
                                </span>
                            </div>
                        )}

                        {allSkipped && (
                            <div className={`fs13 border-start ps-2 ${isBlockedSubset ? 'text-danger' : 'text-warning'}`}>
                                {isBlockedSubset
                                    ? t('The selected employees could not be updated because they already have shared cycles. Please choose different employees.')
                                    : t('The selected employees have cycles that can’t be changed right now. The pattern update will still be saved and applied to future assignments.')
                                }
                            </div>
                        )}

                        <div className="conversation-search mb-1">
                            <input
                                type="text"
                                className="conversation-search-input"
                                placeholder={t('Search Employee')}
                                value={conflictEmployeeSearch}
                                onChange={(e) => setConflictEmployeeSearch(e.target.value)}
                            />
                            <i className="fa fa-search conversation-search-icon" />
                        </div>

                        <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                            <Table bordered size="sm" className="fs13 mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>{t('Employee')}</th>
                                        <th>{t('Template Name')}</th>
                                        <th>{t('Start Date')}</th>
                                        <th>{t('End Date')}</th>
                                        <th>{t('Status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredConflicts.map((conflict, idx) => (
                                        <tr key={idx}>
                                            <td>{conflict.employee_name || '-'}</td>
                                            <td>{conflict.work_pattern_name ? conflict.work_pattern_name : conflict.template_name}</td>
                                            <td>{conflict.cycle_start_date ? shortDateFormat(conflict.cycle_start_date, dateTimeRules) : '-'}</td>
                                            <td>{conflict.cycle_end_date ? shortDateFormat(conflict.cycle_end_date, dateTimeRules) : '-'}</td>
                                            <td>
                                                {conflict.skip_reason
                                                    ? <span className="badge bg-warning text-dark fw-medium">{t(SKIP_REASON_LABEL[conflict.skip_reason] || conflict.skip_reason)}</span>
                                                    : <span className="badge bg-primary text-white fw-medium">{t('Will be replaced')}</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="position-sticky bottom-0 border-top bg-white">
                <Button onClick={handleClose}>{t('Cancel')}</Button>
                {hasConflicts ? (
                    <Button fill disabled={isSaving || isBlockedSubset} onClick={onConfirmAnyway}>
                        {getProceedLabel()}
                    </Button>
                ) : (
                    <Button fill disabled={!applyWorkPatternData.selectedDate || isSaving || applyWorkPatternData.isSubset === null || (applyWorkPatternData.isSubset && !applyWorkPatternData.selectedUserIds?.length)} onClick={handleSave}>
                        {isSaving ? t('Saving...') : t('Apply Changes')}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default EditWorkPatternModal;
